import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, TrendingDown, Clock, Users } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';

const AssistantsCaseExamples: React.FC = () => {
  const caseStudies = [
    {
      industry: 'Accounting',
      title: 'Regional Accounting Firm Reduces Support Load by 65%',
      challenge: 'A 15-person accounting firm was spending 20+ hours per week answering repetitive Xero and GST questions from clients.',
      solution: 'Deployed an Advisory Assistant trained on their FAQ library, Xero documentation, and NZ tax deadlines.',
      results: [
        { icon: TrendingDown, metric: '65%', label: 'Reduction in support queries' },
        { icon: Clock, metric: '18 hrs', label: 'Saved per week' },
        { icon: Users, metric: '24/7', label: 'Client availability' },
      ],
      quote: 'Our team can now focus on high-value advisory work instead of answering the same questions every day.',
      author: 'Practice Manager',
    },
    {
      industry: 'Bookkeeping',
      title: 'Solo Bookkeeper Scales Client Onboarding',
      challenge: 'A solo bookkeeper was struggling to onboard new clients efficiently while managing existing workload.',
      solution: 'Implemented an onboarding assistant that guides new clients through document requirements and software setup.',
      results: [
        { icon: Clock, metric: '70%', label: 'Faster onboarding' },
        { icon: Users, metric: '12', label: 'New clients/month' },
        { icon: TrendingDown, metric: '50%', label: 'Fewer follow-up emails' },
      ],
      quote: 'I can take on more clients without working more hours. The assistant handles the repetitive parts of onboarding.',
      author: 'Business Owner',
    },
    {
      industry: 'Multi-Office Practice',
      title: 'Consistent Client Experience Across 3 Locations',
      challenge: 'A growing practice with 3 offices had inconsistent responses to client queries depending on which staff member responded.',
      solution: 'Deployed a unified Advisory Assistant with approved messaging and escalation to the right team member.',
      results: [
        { icon: Users, metric: '3', label: 'Offices unified' },
        { icon: TrendingDown, metric: '90%', label: 'Response consistency' },
        { icon: Clock, metric: '<2 min', label: 'Average response time' },
      ],
      quote: 'Every client now gets the same quality response, no matter which office they work with.',
      author: 'Managing Partner',
    },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Case Examples
              </h1>
              <p className="text-xl text-white/90">
                See how accounting practices and professional service firms are using 
                Advisory Assistants to transform their client experience.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-16">
              {caseStudies.map((study, index) => (
                <div key={index} className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="p-8 lg:p-10">
                    <span className="text-xs font-medium bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full">
                      {study.industry}
                    </span>
                    <h2 className="text-2xl lg:text-3xl font-bold mt-4 mb-6 text-card-foreground">
                      {study.title}
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                      <div>
                        <h3 className="font-semibold text-card-foreground mb-2">The Challenge</h3>
                        <p className="text-muted-foreground">{study.challenge}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-card-foreground mb-2">The Solution</h3>
                        <p className="text-muted-foreground">{study.solution}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-8">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="bg-muted/50 rounded-lg p-4 text-center">
                          <result.icon className="h-6 w-6 text-brand-teal mx-auto mb-2" />
                          <div className="text-2xl font-bold text-card-foreground">{result.metric}</div>
                          <div className="text-xs text-muted-foreground">{result.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-muted/50 rounded-lg p-6">
                      <Quote className="h-8 w-8 text-brand-teal/30 mb-3" />
                      <p className="text-card-foreground italic mb-3">"{study.quote}"</p>
                      <p className="text-sm text-muted-foreground">— {study.author}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-24 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join these practices in transforming your client experience with an Advisory Assistant.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/assistants/get-assistant">
                <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                  Get Your Assistant
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/assistants/pricing">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsCaseExamples;
